/**
 * Navigation hook with URL synchronization
 */

import { useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import type { RouteParams, SearchParams, NavigationError, UseNavigationResult, NavigateOptions } from '../types/routing'
import { 
  parseRouteParams, 
  parseSearchParams, 
  generateSearchParams, 
  generateRouteUrl,
  cleanUrlParams
} from '../lib/routing/url-utils'

/**
 * Navigation hook providing URL synchronization and routing utilities
 */
export const useNavigation = (): UseNavigationResult => {
  const navigate = useNavigate()
  const location = useLocation()
  
  // Parse current route and search parameters
  const params = useMemo((): RouteParams => {
    return parseRouteParams(location.pathname)
  }, [location.pathname])
  
  const searchParams = useMemo((): SearchParams => {
    return parseSearchParams(location.search)
  }, [location.search])
  
  // Navigation state
  const currentRoute = location.pathname
  const isNavigating = false // Will be enhanced with loading states later
  const error: NavigationError | null = null // Will be enhanced with error handling later
  
  // Navigation actions
  const navigateToUrl = useCallback((to: string, options?: NavigateOptions) => {
    void navigate(to, {
      replace: options?.replace ?? false,
      state: options?.state,
      preventScrollReset: options?.preventScrollReset ?? false
    })
  }, [navigate])
  
  const goBack = useCallback(() => {
    window.history.back()
  }, [])
  
  const goForward = useCallback(() => {
    window.history.forward()
  }, [])
  
  const replace = useCallback((to: string, options?: NavigateOptions) => {
    void navigate(to, {
      ...options,
      replace: true
    })
  }, [navigate])
  
  // URL generation utilities
  const createUrl = useCallback((path: string, searchParams?: SearchParams): string => {
    return generateRouteUrl(path, undefined, searchParams)
  }, [])
  
  const updateSearchParams = useCallback((updates: Partial<SearchParams>) => {
    const currentParams = parseSearchParams(location.search)
    const newParams = { ...currentParams, ...updates }
    const cleanedParams = cleanUrlParams(newParams)
    const searchString = generateSearchParams(cleanedParams)
    const newUrl = location.pathname + searchString
    
    void navigate(newUrl, { replace: true })
  }, [location.pathname, location.search, navigate])
  
  const clearSearchParams = useCallback(() => {
    void navigate(location.pathname, { replace: true })
  }, [location.pathname, navigate])
  
  return {
    // Current state
    currentRoute,
    params,
    searchParams,
    isNavigating,
    error,
    
    // Navigation actions
    navigate: navigateToUrl,
    goBack,
    goForward,
    replace,
    
    // URL utilities
    createUrl,
    updateSearchParams,
    clearSearchParams
  }
}

/**
 * Hook for generating common URLs
 */
export const useUrlGenerators = () => {
  return useMemo(() => ({
    home: (searchParams?: Partial<SearchParams>) => {
      return generateRouteUrl('/', undefined, searchParams)
    },
    
    cpuDetail: (cpuId: string) => {
      return generateRouteUrl('/cpu/:id', { id: cpuId })
    },
    
    comparison: (cpuIds: string[]) => {
      const cpusParam = cpuIds.join(',')
      return generateRouteUrl('/compare', undefined, { cpus: cpusParam })
    },
    
    favorites: (sort?: string) => {
      const params = sort ? { sort: sort as SearchParams['sort'] } as Partial<SearchParams> : undefined
      return generateRouteUrl('/favorites', undefined, params)
    },

    category: (category: string, searchParams?: Partial<SearchParams>) => {
      const cat = category as NonNullable<RouteParams['category']>
      return generateRouteUrl('/category/:category', { category: cat }, searchParams)
    }
  }), [])
}